"use client";

import { useState } from "react";
import type {
  DataColumn,
  Dataset,
} from "@/lib/tool-kit/stats-regression/dataset";
import { isModellable } from "@/lib/tool-kit/stats-regression/dataset";

/**
 * Which column is the response and which are predictors, derived from the pasted data.
 *
 * The reader's choices are kept as raw state, and every render validates them against the
 * current columns: when a new paste changes the shape, a choice that no longer exists
 * falls back to a sensible default instead of pointing at nothing.
 */
export function useModelVariables(
  dataset: Dataset,
  isResponse: (column: DataColumn) => boolean
) {
  const [yChoice, setYChoice] = useState("");
  const [predictorChoice, setPredictorChoice] = useState<number[] | null>(null);

  const responses = dataset.columns.flatMap((column, index) =>
    isResponse(column) ? [index] : []
  );
  const chosen = Number(yChoice);
  const yIndex =
    yChoice !== "" && responses.includes(chosen)
      ? chosen
      : (responses[0] ?? -1);
  const candidates = dataset.columns.flatMap((column, index) =>
    index !== yIndex && isModellable(column) ? [index] : []
  );
  const predictors = (predictorChoice ?? candidates).filter((index) =>
    candidates.includes(index)
  );
  const toggle = (index: number) => {
    setPredictorChoice(
      predictors.includes(index)
        ? predictors.filter((i) => i !== index)
        : [...predictors, index].sort((a, b) => a - b)
    );
  };
  return { responses, yIndex, setYChoice, candidates, predictors, toggle };
}
